import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  type AgentCategoriesResponse,
  type AgentCategory,
  type AIAgent,
  type AIAgentsResponse,
  type AIAgentsSuiteResponse,
  type AIAgentSuite,
} from '@/types/agents';
import {
  CLAIM_AGENT_SUITE_QUERY,
  GET_AI_AGENT_CATEGORIES_QUERY,
  GET_AI_AGENT_SUITES_QUERY,
  GET_AI_AGENTS_QUERY,
} from '@/utils/query-keys';

import {
  useAIAgentsApi,
  useAIAgentsCategoriesApi,
  useAIAgentSuiteApi,
  useClaimAgentSuiteApi,
} from '../services/agentous-agentic-service';

// Hook for fetching AI Agents with React Query
export const useGetAIAgents = (options = {}) => {
  const getAIAgents = useAIAgentsApi();

  return useQuery<AIAgentsResponse, Error, AIAgent[]>(
    [GET_AI_AGENTS_QUERY],
    () => getAIAgents(),
    {
      select: data => {
        // Extract the agents array from the response
        const agents =
          data?.status && data?.data?.aiAgents ? data.data.aiAgents : [];
        // Sort agents alphabetically by name (case-insensitive)
        return [...agents].sort((a, b) =>
          (a.agentName || '').localeCompare(b.agentName || '', undefined, {
            sensitivity: 'base',
          })
        );
      },
      // Agent catalog is effectively static — refresh only on explicit
      // invalidation (e.g. after claiming a suite) or a new tab. To force a
      // refresh for all users after a content edit, bump the persist key
      // version in src/config/queryPersister.ts.
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnMount: false,
      retry: 3,
      ...options,
    }
  );
};

// Single source of truth for the order suites render in across the entire
// site (dropdown, marketplace, suite section, mobile drawer, etc.). Anything
// not in this list falls to the end in original API order.
//
// Drives `useGetAIAgentSuites` below — every consumer that reads from the
// hook gets the same order without having to sort locally. If you need a
// different order in one place, sort the result of the hook there; don't
// add a parallel constant.
export const SUITE_DISPLAY_ORDER = [
  'under-wing',
  'collect-wing',
  'sales-wing',
  'admin-wing',
];

function suiteOrderIndex(key?: string): number {
  if (!key) return Number.MAX_SAFE_INTEGER;
  const idx = SUITE_DISPLAY_ORDER.indexOf(key.toLowerCase());
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
}

// Hook for fetching AI Agent Suites with React Query
export const useGetAIAgentSuites = (options = {}) => {
  const getAIAgentSuites = useAIAgentSuiteApi();

  return useQuery<AIAgentsSuiteResponse, Error, AIAgentSuite[]>(
    [GET_AI_AGENT_SUITES_QUERY],
    () => getAIAgentSuites(),
    {
      select: data => {
        // Extract the agent suites array from the response
        const suites =
          data?.status && data?.data?.aiAgentSuites
            ? data.data.aiAgentSuites
            : [];
        // Normalize: sort each suite's agents alphabetically, then order the
        // suites themselves by SUITE_DISPLAY_ORDER (UnderWing → CollectWing
        // → SalesWing → AdminWing).
        const normalized = suites.map(s => ({
          ...s,
          availableAgents: [...(s.availableAgents || [])].sort((a, b) =>
            (a.agentName || '').localeCompare(b.agentName || '', undefined, {
              sensitivity: 'base',
            })
          ),
        }));
        return normalized.sort(
          (a, b) =>
            suiteOrderIndex(a.agentSuiteKey) - suiteOrderIndex(b.agentSuiteKey)
        );
      },
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnMount: false,
      ...options,
      retry: 1,
    }
  );
};

// Hook for fetching AI Agent Categories with React Query
export const useGetAIAgentCategories = (options = {}) => {
  const getAIAgentCategories = useAIAgentsCategoriesApi();

  return useQuery<AgentCategoriesResponse, Error, AgentCategory[]>(
    [GET_AI_AGENT_CATEGORIES_QUERY],
    () => getAIAgentCategories(),
    {
      select: data => {
        // Extract the categories array from the response
        return data?.status && data?.data ? data.data : [];
      },
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnMount: false,
      retry: 3,
      ...options,
    }
  );
};

// Hook for claiming agents suite with React Query mutation
export const useClaimAgentSuite = (options = {}) => {
  const queryClient = useQueryClient();
  const claimAgentSuite = useClaimAgentSuiteApi();

  return useMutation({
    mutationKey: [CLAIM_AGENT_SUITE_QUERY],
    mutationFn: (agentSuiteKey: string) => claimAgentSuite(agentSuiteKey),
    onSuccess: () => {
      // Invalidate relevant queries after successful claiming
      queryClient.invalidateQueries([GET_AI_AGENT_SUITES_QUERY]);
      queryClient.invalidateQueries([GET_AI_AGENTS_QUERY]);
    },
    ...options,
  });
};

// Convenience hook that fetches both agents and suites
export const useGetAIAgentsData = (options = {}) => {
  const agentsQuery = useGetAIAgents(options);
  const suitesQuery = useGetAIAgentSuites(options);

  return {
    agents: agentsQuery.data || [],
    agentSuites: suitesQuery.data || [],
    isLoadingAgents: agentsQuery.isLoading,
    isLoadingSuites: suitesQuery.isLoading,
    isLoading: agentsQuery.isLoading || suitesQuery.isLoading,
    error: agentsQuery.error || suitesQuery.error,
    refetchAgents: agentsQuery.refetch,
    refetchSuites: suitesQuery.refetch,
    refetchAll: () => {
      agentsQuery.refetch();
      suitesQuery.refetch();
    },
  };
};
